import React from 'react';

const CookiePolicy = () => {
  // Paste your entire Termly HTML code inside the backticks (``) below
  const termlyHtmlContent = `
    <div>
      <h1>Cookie Policy</h1>
      <!-- Paste all remaining Termly HTML content here -->
      This Cookie Policy explains how [Myhouser, Inc.] ("Myhouser," "we," "us," or "our") uses cookies and similar technologies on **myhouser.com** and any related applications, dashboards, and services (collectively, the "Services"). It explains what these technologies are, why we use them, the choices you have, and how to control them.

This Cookie Policy should be read together with our [Privacy Policy] and [Terms of Service], which describe more fully how we collect, use, and protect personal information.

By using the Services, you consent to the use of cookies as described in this policy, except where your consent is required and not yet given. Where consent is required, you can accept, reject, or customize cookies through our cookie consent banner and preference center.

---

## 1. What are cookies and similar technologies?

A **cookie** is a small text file that a website places on your device (computer, tablet, or phone) when you visit. Cookies let the site recognize your device, remember your preferences, keep you signed in, and understand how the Services are used.

We also use technologies that work like cookies, including:

- **Local storage** — data stored in your browser to remember settings and improve performance.
- **Pixels and tags** — tiny snippets of code (sometimes called web beacons) that help us measure usage and the effectiveness of communications.
- **Software development kits (SDKs)** — code in our mobile or desktop apps that performs similar functions to cookies.

In this policy, we refer to all of these collectively as "cookies."

Cookies may be **session cookies** (which expire when you close your browser) or **persistent cookies** (which remain until they expire or you delete them). They may be set by us (**first-party cookies**) or by a third party providing services to us (**third-party cookies**).

---

## 2. Why we use cookies

Because Myhouser is a financial-modeling platform that you sign into and that stores your plans and data, some cookies are essential to keep your account secure and the Services working. Others help us understand and improve the product. Specifically, we use cookies to:

- Keep you signed in and maintain the security and integrity of your session.
- Remember your settings, preferences, and where you left off in your plans.
- Protect against fraud, abuse, and unauthorized access.
- Understand how people use the Services so we can improve them.
- Measure the performance of our content and, where applicable, our marketing.

**We do not use cookies to access, read, or sell the financial details inside your Myhouser plans.** Cookies relate to how you interact with the website and apps, not the content of the financial models you build.

---

## 3. Categories of cookies we use

The table below describes the categories of cookies we use. The specific cookies, providers, and durations in each category are listed and kept current in our **cookie preference center**, which is generated by our consent management tool. *(Populate the specific cookie names, providers, purposes, and durations from your cookie-scanner output — e.g., Cookiebot — before publishing.)*

| Category | Purpose | Consent required? | Examples |
|---|---|---|---|
| **Strictly necessary** | Enable core functions like signing in, account security, session management, load balancing, and remembering consent choices. The Services cannot function properly without these. | No (always active) | Authentication/session token, security/anti-fraud, consent record |
| **Functional** | Remember your preferences and choices (e.g., language, saved views, recently opened plans) to give you a more personalized experience. | Yes | Preference and personalization cookies |
| **Performance & analytics** | Help us understand how visitors use the Services — which pages are visited, errors encountered, and how features perform — on an aggregated basis. | Yes | [Google Analytics], product-analytics provider |
| **Targeting & advertising** | Used to measure and, where applicable, deliver marketing, and to limit how often you see an ad. *(Include this row only if you actually run advertising or marketing pixels.)* | Yes | [Advertising/marketing pixels, if used] |

---

## 4. Third-party cookies

Some cookies are placed by third parties that provide services to us, such as analytics, security, customer support, or content delivery. These third parties may set their own cookies and process data according to their own privacy policies. We do not control these third-party cookies. A current list of third-party providers and links to their privacy policies is available in our cookie preference center.

Examples of the types of third parties we may use include [analytics providers, e.g., Google Analytics], [customer support tools], [security/anti-fraud providers], and [content delivery networks].

---

## 5. Your choices and how to manage cookies

You have several ways to control cookies:

**Consent banner and preference center.** When you first visit the Services (and at any time afterward via the "[Cookie Settings]" link in our footer), you can accept, reject, or customize non-essential cookies. Strictly necessary cookies cannot be switched off because the Services depend on them.

**Browser controls.** Most browsers let you block or delete cookies through their settings. Because the method differs by browser, see your browser's help pages for instructions. Note that blocking strictly necessary cookies may prevent you from signing in or using key features.

**Do Not Track and Global Privacy Control.** Some browsers offer a "Do Not Track" signal or a Global Privacy Control (GPC) signal. We honor recognized opt-out preference signals, including GPC, where required by law, as described in the California section below.

**Analytics opt-outs.** Where we use [Google Analytics], you can opt out using the [Google Analytics Opt-out Browser Add-on].

---

## 6. California privacy rights (CCPA/CPRA)

If you are a California resident, the California Consumer Privacy Act, as amended by the California Privacy Rights Act (together, the "CCPA"), gives you specific rights regarding your personal information.

Certain uses of cookies — particularly analytics and advertising cookies that share information with third parties — may be considered a "sale" or "sharing" of personal information under the CCPA, even when no money changes hands. *(Confirm with counsel whether your specific cookie configuration constitutes a "sale" or "share.")*

You have the right to opt out of the sale or sharing of your personal information. You can exercise this right by:

- Using the "**[Do Not Sell or Share My Personal Information]**" link in our website footer;
- Setting your preferences in our cookie preference center; or
- Enabling the Global Privacy Control (GPC) signal in your browser, which we treat as a valid opt-out request for the browser and device on which it is enabled.

California residents also have rights to know, access, correct, and delete personal information, and the right not to be discriminated against for exercising these rights. To learn more or to submit a request, see our [Privacy Policy] or contact us using the details below.

---

## 7. Cookies and users outside the United States

If you access the Services from outside the United States, please be aware that your information may be processed in the United States. Where required by law (for example, under the EU/UK GDPR and ePrivacy rules), we will obtain your consent before placing non-essential cookies and will rely on that consent as our legal basis for using them. You may withdraw your consent at any time through the cookie preference center. *(Include and tailor this section only if you serve users in these regions.)*

---

## 8. Changes to this Cookie Policy

We may update this Cookie Policy from time to time to reflect changes in the cookies we use or for legal, operational, or regulatory reasons. When we make material changes, we will update the "Last updated" date above and, where appropriate, notify you or seek your consent again. We encourage you to review this policy periodically.

---

## 9. Contact us

If you have questions about this Cookie Policy or our use of cookies, please contact us at:

Myhouser, Inc.
6379 Firefly Dr., San Jose, CA 95120
Email: privacy@myhouser.com

---

*This document is a template prepared for Myhouser.com. The specific cookies, providers, durations, and legal characterizations must be completed and reviewed by qualified legal counsel before publication.*
    </div>
  `;

  return (
    <div 
      className="cookie-policy-container" 
      dangerouslySetInnerHTML={{ __html: termlyHtmlContent }} 
    />
  );
};

export default CookiePolicy;